import { Link } from 'react-router-dom';
import { useStats, useAdminSessions } from '../../api/admin';
import { Page, PageHeader } from '../../components/Layout';
import { StatTile } from '../../components/charts/StatTile';
import { CompositionBar } from '../../components/charts/CompositionBar';
import { StatusBars } from '../../components/charts/StatusBars';
import { DayTimeline } from '../../components/charts/DayTimeline';
import { ErrorState } from '../../components/EmptyState';
import { Spinner } from '../../components/Spinner';
import { formatNumber, formatRelative } from '../../lib/format';
import { ApiError } from '../../lib/api';
import { TodayWorklist } from './TodayWorklist';

export function DashboardPage() {
  const stats = useStats();
  // The timeline needs the actual bookings, not counts — a generous page so
  // a busy few hours is never silently truncated.
  const upcoming = useAdminSessions({ scope: 'upcoming', limit: 100 });

  if (stats.isError) {
    return (
      <Page>
        <ErrorState
          title="Could not load the dashboard"
          message={stats.error instanceof ApiError ? stats.error.message : undefined}
          onRetry={() => void stats.refetch()}
        />
      </Page>
    );
  }

  const s = stats.data;

  return (
    <Page>
      <PageHeader
        title="Overview"
        description={
          s
            ? `Live as of ${formatRelative(s.generatedAt)}. Refreshes every 30 seconds.`
            : 'Loading the current picture…'
        }
        actions={
          stats.isFetching ? (
            <span className="flex items-center gap-1.5 text-xs text-ink-3">
              <Spinner /> Refreshing
            </span>
          ) : null
        }
      />

      {/* What needs someone first; the live picture follows. */}
      <TodayWorklist />

      {/* What is happening right now. */}
      <section className="mb-4 grid gap-3 sm:grid-cols-3">
        <StatTile
          label="People online"
          value={s?.live.onlineUsers ?? '—'}
          hint="Connected right now"
          tone={s && s.live.onlineUsers > 0 ? 'live' : 'default'}
        />
        <StatTile
          label="Lessons in progress"
          value={s?.live.sessionsInProgress ?? '—'}
          hint="A call is open"
          tone={s && s.live.sessionsInProgress > 0 ? 'live' : 'default'}
        />
        <StatTile
          label="Starting within the hour"
          value={s?.live.startingWithinHour ?? '—'}
          hint="Scheduled to begin soon"
        />
      </section>

      <section className="mb-4">
        {upcoming.isLoading ? (
          <div className="panel flex h-48 items-center justify-center text-ink-3">
            <Spinner className="size-5" />
          </div>
        ) : (
          <DayTimeline sessions={upcoming.data?.data ?? []} />
        )}
      </section>

      <section className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Lessons booked"
          value={s?.sessions.bookedLast7Days ?? '—'}
          hint="In the last 7 days"
        />
        <StatTile
          label="Messages sent"
          value={s?.messagesLast7Days ?? '—'}
          hint="In the last 7 days"
        />
        <StatTile
          label="New people"
          value={s?.users.newLast7Days ?? '—'}
          hint="Signed up in the last 7 days"
        />
        <StatTile
          label="Cancellation rate"
          value={s ? `${s.sessions.cancellationRatePct}%` : '—'}
          hint="Of lessons that have concluded"
        />
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="panel p-4">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-ink">People</h2>
            <Link to="/users" className="text-xs text-accent hover:underline">
              Manage people
            </Link>
          </div>

          {s ? (
            <>
              <CompositionBar
                total={s.users.total}
                segments={[
                  { label: 'Students', value: s.users.students, color: 'var(--series-1)' },
                  { label: 'Tutors', value: s.users.tutors, color: 'var(--series-2)' },
                  { label: 'Admins', value: s.users.admins, color: 'var(--series-3)' },
                ]}
              />
              <dl className="mt-5 grid grid-cols-3 gap-3 border-t border-rule pt-4 text-xs">
                <div>
                  <dt className="text-ink-3">Total</dt>
                  <dd className="tabular mt-0.5 text-base font-medium text-ink">
                    {formatNumber(s.users.total)}
                  </dd>
                </div>
                <div>
                  <dt className="text-ink-3">Verified tutors</dt>
                  <dd className="tabular mt-0.5 text-base font-medium text-ink">
                    {formatNumber(s.users.verifiedTutors)}
                    <span className="ml-1 text-xs font-normal text-ink-3">
                      of {formatNumber(s.users.tutors)}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-ink-3">Suspended</dt>
                  <dd className="tabular mt-0.5 text-base font-medium text-ink">
                    {formatNumber(s.users.suspended)}
                  </dd>
                </div>
              </dl>
            </>
          ) : (
            <div className="skeleton h-28" />
          )}
        </div>

        <div className="panel p-4">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="text-sm font-semibold text-ink">Lessons by state</h2>
            <Link to="/sessions" className="text-xs text-accent hover:underline">
              Browse lessons
            </Link>
          </div>

          {s ? (
            <StatusBars
              rows={[
                { label: 'Scheduled', value: s.sessions.scheduled, dotColor: 'var(--accent)' },
                { label: 'In progress', value: s.sessions.inProgress, dotColor: 'var(--live)' },
                { label: 'Completed', value: s.sessions.completed, dotColor: 'var(--good)' },
                { label: 'Cancelled', value: s.sessions.cancelled, dotColor: 'var(--danger)' },
                { label: 'No show', value: s.sessions.noShow, dotColor: 'var(--ink-3)' },
              ]}
            />
          ) : (
            <div className="skeleton h-28" />
          )}
        </div>
      </section>
    </Page>
  );
}
